import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { featuresApi } from '@/utils/api';
import type { Feature, ApiResponse } from '@/types';

interface FeaturesState {
  features: Feature[];
  currentFeature: Feature | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
  filters: {
    epicId?: string;
    leadProgramId?: string;
    targetQuarter?: number;
    status?: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const initialState: FeaturesState = {
  features: [],
  currentFeature: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
};

// Async thunks
export const fetchFeatures = createAsyncThunk(
  'features/fetchFeatures',
  async (params?: {
    epicId?: string;
    leadProgramId?: string;
    targetQuarter?: number;
    status?: string;
    page?: number;
    limit?: number;
  }, { rejectWithValue }) => {
    const response = await featuresApi.getAll(params);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch features');
    }
    return response.data;
  }
);

export const fetchFeatureById = createAsyncThunk(
  'features/fetchFeatureById',
  async (id: string, { rejectWithValue }) => {
    const response = await featuresApi.getById(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch feature');
    }
    return (response.data as any)?.feature ?? response.data;
  }
);

export const createFeature = createAsyncThunk(
  'features/createFeature',
  async (featureData: Partial<Feature>, { rejectWithValue }) => {
    const response = await featuresApi.create(featureData);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to create feature');
    }
    return (response.data as any)?.feature ?? response.data;
  }
);

export const updateFeature = createAsyncThunk(
  'features/updateFeature',
  async ({ id, data }: { id: string; data: Partial<Feature> }, { rejectWithValue }) => {
    const response = await featuresApi.update(id, data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update feature');
    }
    return (response.data as any)?.feature ?? response.data;
  }
);

export const deleteFeature = createAsyncThunk(
  'features/deleteFeature',
  async (id: string, { rejectWithValue }) => {
    const response = await featuresApi.delete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to delete feature');
    }
    return id;
  }
);

export const completeFeature = createAsyncThunk(
  'features/completeFeature',
  async (id: string, { rejectWithValue }) => {
    const response = await featuresApi.complete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to complete feature');
    }
    return (response.data as any)?.feature ?? response.data;
  }
);

export const reopenFeature = createAsyncThunk(
  'features/reopenFeature',
  async (id: string, { rejectWithValue }) => {
    const response = await featuresApi.reopen(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to reopen feature');
    }
    return (response.data as any)?.feature ?? response.data;
  }
);

const featuresSlice = createSlice({
  name: 'features',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<FeaturesState['filters']>>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentFeature: (state, action: PayloadAction<Feature | null>) => {
      state.currentFeature = action.payload;
    },
    setPagination: (state, action: PayloadAction<Partial<FeaturesState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch features
      .addCase(fetchFeatures.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeatures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.features = action.payload.features || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchFeatures.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch feature by ID
      .addCase(fetchFeatureById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeatureById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentFeature = action.payload;
      })
      .addCase(fetchFeatureById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create feature
      .addCase(createFeature.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createFeature.fulfilled, (state, action) => {
        state.isCreating = false;
        state.features.unshift(action.payload);
      })
      .addCase(createFeature.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update feature
      .addCase(updateFeature.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateFeature.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.features.findIndex(feature => feature.id === action.payload.id);
        if (index !== -1) {
          state.features[index] = action.payload;
        }
        if (state.currentFeature?.id === action.payload.id) {
          state.currentFeature = action.payload;
        }
      })
      .addCase(updateFeature.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete feature
      .addCase(deleteFeature.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteFeature.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.features = state.features.filter(feature => feature.id !== action.payload);
        if (state.currentFeature?.id === action.payload) {
          state.currentFeature = null;
        }
      })
      .addCase(deleteFeature.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Complete feature
      .addCase(completeFeature.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(completeFeature.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.features.findIndex(feature => feature.id === action.payload.id);
        if (index !== -1) {
          state.features[index] = action.payload;
        }
        if (state.currentFeature?.id === action.payload.id) {
          state.currentFeature = action.payload;
        }
      })
      .addCase(completeFeature.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Reopen feature
      .addCase(reopenFeature.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(reopenFeature.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.features.findIndex(feature => feature.id === action.payload.id);
        if (index !== -1) {
          state.features[index] = action.payload;
        }
        if (state.currentFeature?.id === action.payload.id) {
          state.currentFeature = action.payload;
        }
      })
      .addCase(reopenFeature.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  setFilters, 
  clearFilters, 
  setCurrentFeature, 
  setPagination 
} = featuresSlice.actions;

export default featuresSlice.reducer;
